import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Info } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';
import { Spinner } from '@repo/ui/src/components/ui/spinner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@repo/ui/src/components/ui/dialog';
import type { OpenPeriodBlock } from '@repo/shared-types';
import { useTemplatePreview, useApplyTemplate } from '../../hooks/useScheduleTemplates';

/** "YYYY-MM" -> "MM/YYYY" — `periodLabel` is already zero-padded, so a
 *  straight split-and-swap is the whole job. */
function toMonthYear(periodLabel: string): string {
  const [year, month] = periodLabel.split('-');
  return `${month}/${year}`;
}

/** The "Áp dụng…" dialog behind the template banner — previews the diff a
 *  template would make against reality, warns per archived-slot group, and
 *  names the moment to press it. Not an AlertDialog: it holds a preview to
 *  read, not a plain yes/no.
 *
 *  Three states:
 *   - loading — `preview` is in flight (fired fresh on every open, never
 *     cached — see useTemplatePreview).
 *   - blocked — a previous month is still open. The confirm button is not
 *     rendered at all: a hard stop should not look like something to argue
 *     with. Can arrive two ways — the preview says so up front, or a 409
 *     from `apply` says so after the preview had gone stale (someone else
 *     closed/reopened a month between open and confirm); either source
 *     renders the identical blocked body.
 *   - ready — counts, orphan warnings, guidance, and the real confirm. */
export function ApplyTemplateDialog({
  open, onOpenChange, templateId, onApplied,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  templateId: string;
  /** Fired after a successful apply — the caller exits template mode
   *  (`setTemplateId(null)`) so the teacher lands back on the reality they
   *  just created. */
  onApplied: () => void;
}) {
  const { t } = useTranslation('scheduling');
  const navigate = useNavigate();
  const preview = useTemplatePreview(templateId);
  const apply = useApplyTemplate(templateId);

  // A 409 from `apply` carries its own `block`, which must win over
  // whatever the (now-stale) preview said — see useApplyTemplate's comment.
  const [runtimeBlock, setRuntimeBlock] = useState<OpenPeriodBlock | null>(null);

  useEffect(() => {
    if (!open) return;
    setRuntimeBlock(null);
    preview.mutate();
    // preview is a fresh useMutation object each render; keying the effect
    // on open/templateId (not on `preview`) is what makes this fire exactly
    // once per open rather than looping.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateId]);

  const block = runtimeBlock ?? preview.data?.blockedByOpenPeriod ?? null;
  const loading = preview.isPending;
  const data = preview.data;
  const noChanges = data ? data.joins.length + data.leaves.length === 0 : false;

  const handleApply = () => {
    apply.mutate({ backupNamePrefix: t('template.backupPrefix') }, {
      onSuccess: (result) => {
        toast.success(
          result.backupTemplateName
            ? t('template.applyDialog.toastWithBackup', { name: result.backupTemplateName })
            : t('template.applyDialog.toast', { joined: result.joined, left: result.left }),
        );
        onOpenChange(false);
        onApplied();
      },
      onError: (err: Error & { code?: string; block?: OpenPeriodBlock }) => {
        if (err.code === 'OPEN_PERIOD' && err.block) {
          setRuntimeBlock(err.block);
        } else {
          toast.error(err.message || t('template.applyDialog.title'));
        }
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>{t('template.applyDialog.title')}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-surface-500">
            <Spinner /> {t('template.applyDialog.loading')}
          </div>
        ) : block ? (
          <>
            <p className="py-2 text-sm text-destructive">
              {t('template.applyDialog.blocked', { m: toMonthYear(block.periodLabel) })}
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('template.applyDialog.cancel')}
              </Button>
              <Button type="button" onClick={() => navigate('/invoices/close-month')}>
                {t('template.applyDialog.goClose')}
              </Button>
            </DialogFooter>
          </>
        ) : data ? (
          <>
            <div className="space-y-3 py-2">
              {noChanges ? (
                <p className="text-sm text-surface-600">{t('template.applyDialog.noChanges')}</p>
              ) : (
                <>
                  <p className="text-sm font-medium text-surface-700">
                    {t('template.applyDialog.counts', {
                      joins: data.joins.length,
                      leaves: data.leaves.length,
                      unchanged: data.unchanged,
                    })}
                  </p>
                  <p className="text-xs text-surface-400">{t('template.applyDialog.backupNote')}</p>
                </>
              )}
              {data.skippedOrphans.map((orphan) => (
                <div
                  key={orphan.slotLabel}
                  className="flex items-start gap-2 rounded-md border border-attention/30 bg-attention-tint px-3 py-2"
                >
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-attention-foreground" strokeWidth={1.9} />
                  <p className="text-sm text-attention-foreground">
                    {t('template.applyDialog.orphanWarning', { label: orphan.slotLabel, n: orphan.count })}
                  </p>
                </div>
              ))}
              <p className="text-xs text-surface-400">{t('template.applyDialog.guidance')}</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('template.applyDialog.cancel')}
              </Button>
              <Button type="button" onClick={handleApply} disabled={apply.isPending}>
                {apply.isPending ? t('template.applyDialog.applying') : t('template.applyDialog.confirm')}
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
